/**
 * PostHog E-Commerce Tracking
 *
 * Centralized tracking module that listens to ThemeEvents dispatched by
 * existing components. No component files are modified — all wiring
 * happens through document-level event listeners.
 *
 * Events captured:
 *   ad_landing, product_viewed, add_to_cart, cart_viewed,
 *   checkout_started, variant_selected, search_performed,
 *   search_result_clicked
 */

if (!window.Shopify?.designMode) {
	initEcommerceTracking();
}

function initEcommerceTracking() {
	trackAttribution();
	trackPageEvents();
	trackCartEvents();
	trackVariantEvents();
	trackSearchEvents();
	trackCheckout();
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getPageData() {
	const el = document.getElementById('posthog-ecommerce-data');
	if (!el) return null;
	try {
		return JSON.parse(el.textContent);
	} catch {
		return null;
	}
}

function capture(event, props) {
	window.posthog?.capture(event, props);
}

// ---------------------------------------------------------------------------
// 1. Attribution — UTM / GCLID capture
// ---------------------------------------------------------------------------

function trackAttribution() {
	const params = new URLSearchParams(window.location.search);
	const keys = [
		'utm_source',
		'utm_medium',
		'utm_campaign',
		'utm_content',
		'utm_term',
		'gclid',
		'gbraid',
		'wbraid',
	];
	const adProps = {};

	for (const key of keys) {
		const val = params.get(key);
		if (val) adProps[key] = val;
	}

	if (Object.keys(adProps).length === 0) return;

	// Guard: don't re-fire after View Transition reloads the module
	const firedKey = '__ph_ad_landing_fired';
	if (sessionStorage.getItem(firedKey)) return;
	sessionStorage.setItem(firedKey, '1');

	// Attach UTM data as super-properties for the entire session
	window.posthog?.register(adProps);

	// Fire ad_landing with first-touch and last-touch attribution
	capture('ad_landing', {
		landing_page: window.location.pathname,
		...adProps,
		$set_once: {
			first_utm_source: adProps.utm_source,
			first_utm_medium: adProps.utm_medium,
			first_utm_campaign: adProps.utm_campaign,
			first_utm_term: adProps.utm_term,
			first_gclid: adProps.gclid,
			first_landing_page: window.location.pathname,
		},
		$set: {
			last_utm_source: adProps.utm_source,
			last_utm_medium: adProps.utm_medium,
			last_utm_campaign: adProps.utm_campaign,
			last_utm_term: adProps.utm_term,
			last_gclid: adProps.gclid,
		},
	});
}

// ---------------------------------------------------------------------------
// 2. Page events — product_viewed, cart_viewed
// ---------------------------------------------------------------------------

function trackPageEvents() {
	const data = getPageData();
	if (!data) return;

	if (data.product) {
		capture('product_viewed', {
			product_id: data.product.id,
			product_title: data.product.title,
			product_handle: data.product.handle,
			product_type: data.product.type,
			product_vendor: data.product.vendor,
			price: data.product.price / 100,
			variant_id: data.product.variant_id,
			variant_title: data.product.variant_title,
			currency: data.product.currency,
		});
	}

	if (data.cart) {
		capture('cart_viewed', {
			cart_total: data.cart.total / 100,
			item_count: data.cart.item_count,
			currency: data.cart.currency,
			items: data.cart.items.map((i) => ({
				product_id: i.product_id,
				title: i.title,
				quantity: i.quantity,
				price: i.price / 100,
			})),
		});
	}
}

// ---------------------------------------------------------------------------
// 3. Cart events — add_to_cart
// ---------------------------------------------------------------------------

function trackCartEvents() {
	document.addEventListener('cart:update', (event) => {
		const detail = event.detail;
		if (!detail?.data) return;

		// Only capture add-to-cart, not quantity updates from the cart page
		if (detail.data.source !== 'product-form-component') return;
		if (detail.data.didError) return;

		const props = {
			product_id: detail.data.productId,
			variant_id: detail.data.variantId,
			item_count: detail.data.itemCount,
		};

		// Try to enrich with server-side data (available on PDP)
		const pageData = getPageData();
		if (
			pageData?.product &&
			String(pageData.product.id) === String(detail.data.productId)
		) {
			props.product_title = pageData.product.title;
			props.product_handle = pageData.product.handle;
			props.product_type = pageData.product.type;
			props.price = pageData.product.price / 100;
			props.currency = pageData.product.currency;
			props.method = 'pdp';
		} else {
			// Quick-add or collection page — get what we can from the DOM
			const formEl = document.querySelector(
				`product-form-component[data-product-id="${detail.data.productId}"]`
			);
			if (formEl) {
				props.product_url = formEl.dataset.productUrl;
			}
			props.method = 'quick_add';
		}

		capture('add_to_cart', props);
	});
}

// ---------------------------------------------------------------------------
// 4. Variant events — variant_selected
// ---------------------------------------------------------------------------

function trackVariantEvents() {
	document.addEventListener('variant:update', (event) => {
		const variant = event.detail?.resource;
		if (!variant) return;

		capture('variant_selected', {
			variant_id: variant.id,
			variant_available: variant.available,
			variant_sku: variant.sku,
			product_id: event.detail.data?.productId,
		});
	});
}

// ---------------------------------------------------------------------------
// 5. Search events — search_performed, search_result_clicked
// ---------------------------------------------------------------------------

function trackSearchEvents() {
	// Full-page search results
	if (typeof Theme !== 'undefined' && Theme.template.name.includes('search')) {
		const q = new URLSearchParams(window.location.search).get('q');
		if (q) {
			capture('search_performed', {
				query: q,
				search_type: 'full_page',
			});
		}
	}

	// Predictive search — debounced input listener
	let debounceTimer;
	document.addEventListener(
		'input',
		(event) => {
			const input = event.target;
			if (
				input.name !== 'q' ||
				!input.closest('predictive-search-component')
			)
				return;

			clearTimeout(debounceTimer);
			debounceTimer = setTimeout(() => {
				if (input.value.length >= 2) {
					capture('search_performed', {
						query: input.value,
						search_type: 'predictive',
					});
				}
			}, 800);
		},
		true
	);

	// Search result clicked — delegated click listener
	document.addEventListener(
		'click',
		(event) => {
			const link = event.target.closest(
				'predictive-search-component a[href]'
			);
			if (!link) return;

			const card = link.closest('[data-product-id]');
			capture('search_result_clicked', {
				url: link.href,
				product_id: card?.dataset.productId,
				query: document.querySelector(
					'predictive-search-component input[name="q"]'
				)?.value,
			});
		},
		true
	);
}

// ---------------------------------------------------------------------------
// 6. Checkout — checkout_started
// ---------------------------------------------------------------------------

function trackCheckout() {
	document.addEventListener(
		'submit',
		(event) => {
			const form = event.target;
			if (form.id !== 'cart-form') return;

			// Only fire when the checkout button was the submitter
			const submitter = event.submitter;
			if (!submitter || submitter.name !== 'checkout') return;

			const pageData = getPageData();
			capture('checkout_started', {
				cart_total: pageData?.cart?.total
					? pageData.cart.total / 100
					: undefined,
				item_count: pageData?.cart?.item_count,
				currency: pageData?.cart?.currency,
			});
		},
		{ capture: true }
	);
}
